import React, { useRef, useEffect } from 'react';
import {
    Animated,
    Easing,
    Pressable,
    PressableProps,
    StyleProp,
    ViewStyle,
    View,
    StyleSheet,
} from 'react-native';

type Props = Omit<PressableProps, 'children' | 'style'> & {
    borderRadius?: number;
    scaleTo?: number;
    fadeTo?: number;
    rippleColor?: string;
    pressedTintColor?: string;

    frameClassName?: string;
    pressedFrameClassName?: string;
    frameStyle?: StyleProp<ViewStyle>;

    contentClassName?: string;
    contentStyle?: StyleProp<ViewStyle>;

    children: React.ReactNode | ((s: { pressed: boolean }) => React.ReactNode);
};

export default function PressableScale({
    borderRadius = 20,
    scaleTo = 0.97,
    fadeTo = 0.95,
    rippleColor = '#14141414',
    pressedTintColor = '#1677FF29',
    disabled,
    onPress,
    onPressIn,
    onPressOut,

    frameClassName,
    pressedFrameClassName,
    frameStyle,

    contentClassName,
    contentStyle,

    children,
    ...rest
}: Props) {
    const scale = useRef(new Animated.Value(1)).current;
    const fade = useRef(new Animated.Value(1)).current;
    const overlay = useRef(new Animated.Value(0)).current;

    useEffect(() => {
        scale.setValue(1);
        fade.setValue(1);
        overlay.setValue(0);
    }, [scale, fade, overlay]);

    const animateIn = () => {
        if (disabled) return;

        Animated.parallel([
            Animated.timing(scale, {
                toValue: scaleTo,
                duration: 90,
                easing: Easing.out(Easing.quad),
                useNativeDriver: true
            }),
            Animated.timing(fade, {
                toValue: fadeTo,
                duration: 90,
                useNativeDriver: true
            }),
            Animated.timing(overlay, {
                toValue: 1,
                duration: 90,
                useNativeDriver: true
            }),
        ]).start();
    };

    const animateOut = () => {
        Animated.parallel([
            Animated.spring(scale, {
                toValue: 1,
                useNativeDriver: true,
                friction: 6,
                tension: 220
            }),
            Animated.timing(fade, {
                toValue: 1,
                duration: 120,
                useNativeDriver: true
            }),
            Animated.timing(overlay, {
                toValue: 0,
                duration: 120,
                useNativeDriver: true
            }),
        ]).start();
    };

    return (
        <Pressable
            {...rest}
            disabled={disabled}
            onPress={onPress}
            onPressIn={(e) => { if (!disabled) animateIn(); onPressIn?.(e); }}
            onPressOut={(e) => { if (!disabled) animateOut(); onPressOut?.(e); }}
            android_ripple={!disabled ? { color: rippleColor, borderless: false, foreground: true } : undefined}
            style={[{ borderRadius, overflow: 'hidden' }]}
        >
            {({ pressed }) => (
                <View
                    style={[{ borderRadius: borderRadius }, frameStyle]}
                    className={`${frameClassName ?? ''} ${pressed && !disabled ? (pressedFrameClassName ?? '') : ''}`}
                >
                    <Animated.View
                        pointerEvents="none"
                        style={[
                            StyleSheet.absoluteFill,
                            { opacity: overlay, backgroundColor: pressedTintColor, borderRadius: borderRadius},
                        ]}
                    />

                    <Animated.View
                        style={[
                            {
                                transform: [{ scale }],
                                opacity: disabled ? 0.7 : fade,
                                flex: 1,
                                justifyContent: 'center',
                            },
                            contentStyle
                        ]}
                        className={contentClassName}
                    >
                        {typeof children === 'function' ? children({ pressed }) : children}
                    </Animated.View>

                </View>
            )}
        </Pressable>
    );
}
